/**
 * BLU-1462: client QC redo on the tracker. A Hand over client found faulty
 * garments after the hand over; a coordinator picks them and the same order
 * reopens, each picked garment gets a redo, and the order is handed over
 * again.
 *
 * Entry point only. The server re-checks everything under row locks (the
 * owner-floor fence, the order is handed over, the client is on Hand over,
 * each garment is SHIPPED and printed); these helpers keep the tracker from
 * offering what the server is certain to refuse.
 */
import type { RouterOutputs } from '@/lib/trpc';

/** `prod_garment_units.redo_reason` on a client QC replacement (backend CLIENT_QC_REDO_REASON). */
export const CLIENT_QC_REDO_REASON = 'client_qc';

/** Mirrors the backend zod bounds (CLIENT_QC_REDO_MAX_GARMENTS / CLIENT_QC_REDO_NOTE_MAX). */
export const CLIENT_QC_REDO_MAX_GARMENTS = 50;
export const CLIENT_QC_REDO_NOTE_MAX = 300;

/** The slice of a tracker garment the picker reads. */
export interface QcRedoGarment {
  id: string;
  status: string;
  productionMethod: string;
}

/**
 * The garments the dialog lists: SHIPPED and printed. The payload maps a
 * BLANK- wrapper to PAP, so `productionMethod === 'POD'` covers both of the
 * server's not_pod refusals.
 */
export function qcRedoEligibleGarments<T extends QcRedoGarment>(garments: readonly T[]): T[] {
  return garments.filter((g) => g.status === 'SHIPPED' && g.productionMethod === 'POD');
}

type QcRedoResult = RouterOutputs['productionOrders']['createClientQcRedo'];
export type ClientQcRedoRefusal = Extract<QcRedoResult, { ok: false }>['reason'];

/** Literal keys (not a template string) so check:i18n can see them. */
export const QC_REDO_REFUSAL_KEYS: Record<ClientQcRedoRefusal, string> = {
  not_found: 'tracker.clientQcRedo.refusal.orderGone',
  order_cancelled: 'tracker.clientQcRedo.refusal.orderGone',
  redo_open: 'tracker.clientQcRedo.refusal.redoOpen',
  not_handed_over: 'tracker.clientQcRedo.refusal.notHandedOver',
  not_handover_client: 'tracker.clientQcRedo.refusal.notHandoverClient',
  garment_not_eligible: 'tracker.clientQcRedo.refusal.garmentNotEligible',
  not_pod: 'tracker.clientQcRedo.refusal.notPod',
};
